import { useState } from 'react';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import {
  Award, Calendar, Building2, Trees, Weight, Download,
  CheckCircle, XCircle, Clock, Loader2, QrCode
} from 'lucide-react';
import type { EntitlementCertificate } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';
import PbrMark from './PbrMark';
import PbrNotice from './PbrNotice';
import { formatNumeric } from '@/utils/numeric';

interface CertificateCardProps {
  certificate: EntitlementCertificate;
}

const STATUS_CONFIG: Record<string, {
  label: string;
  icon: typeof CheckCircle;
  bgColor: string;
  textColor: string;
}> = {
  active: {
    label: 'Valid',
    icon: CheckCircle,
    bgColor: 'bg-success-100',
    textColor: 'text-success-700',
  },
  expired: {
    label: 'Expired',
    icon: Clock,
    bgColor: 'bg-neutral-100',
    textColor: 'text-neutral-600',
  },
  revoked: {
    label: 'Revoked',
    icon: XCircle,
    bgColor: 'bg-error-100',
    textColor: 'text-error-700',
  },
  superseded: {
    label: 'Superseded',
    icon: Clock,
    bgColor: 'bg-warning-100',
    textColor: 'text-warning-700',
  },
};

function CertificateCard({ certificate }: CertificateCardProps) {
  const timezone = useOrgTimezone();
  const [downloading, setDownloading] = useState(false);

  const statusConfig = STATUS_CONFIG[certificate.status] || STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;

  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'N/A';
    return formatLocalDateInTz(date, timezone);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await cultivarService.downloadCertificatePDF(certificate.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${certificate.certificate_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading certificate:', err);
      alert('Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  const verificationUrl = `${window.location.origin}/verify/${certificate.verification_token}`;

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-primary-100 flex items-center justify-center">
              <Award className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h3 className="font-bold text-neutral-900">
                {certificate.certificate_number}
              </h3>
              <p className="text-sm text-neutral-500">
                {certificate.cultivar_name}
                {/* Entitlement certificates only exist for PBR-protected varieties — force the mark. */}
                <PbrMark status="protected" />
              </p>
            </div>
          </div>
          <div className={`
            flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium
            ${statusConfig.bgColor} ${statusConfig.textColor}
          `}>
            <StatusIcon className="h-4 w-4" />
            {statusConfig.label}
          </div>
        </div>

        {/* License Info */}
        <div className="mb-4 pb-4 border-b border-neutral-100">
          <p className="text-sm text-neutral-600">
            <span className="font-medium">{certificate.license_tier}</span> License
          </p>
          <div className="flex items-center gap-2 mt-1 text-sm text-neutral-500">
            <Building2 className="h-3.5 w-3.5" />
            <span>{certificate.licensee_org_name}</span>
            <span>•</span>
            <span className="text-neutral-400">via</span>
            <span>{certificate.licensor_org_name}</span>
          </div>
          {certificate.region && (
            <p className="text-sm text-neutral-500 mt-1">
              Region: {certificate.region}
            </p>
          )}
        </div>

        {/* Declared Volumes */}
        <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-neutral-50 rounded-lg">
          <div className="text-center">
            <Trees className="h-4 w-4 text-success-500 mx-auto mb-1" />
            <p className="text-sm font-bold text-neutral-900">
              {formatNumeric(certificate.declared_trees)}
            </p>
            <p className="text-xs text-neutral-500">Trees</p>
          </div>
          <div className="text-center">
            <Weight className="h-4 w-4 text-warning-500 mx-auto mb-1" />
            <p className="text-sm font-bold text-neutral-900">
              {formatNumeric(certificate.declared_production_tonnes)}
            </p>
            <p className="text-xs text-neutral-500">Production (t)</p>
          </div>
          <div className="text-center">
            <Calendar className="h-4 w-4 text-primary-500 mx-auto mb-1" />
            <p className="text-sm font-bold text-neutral-900">
              {certificate.season_year || '-'}
            </p>
            <p className="text-xs text-neutral-500">Season</p>
          </div>
        </div>

        {/* Validity */}
        <div className="flex items-center justify-between text-sm text-neutral-600 mb-4">
          <span>Valid: {formatDate(certificate.valid_from)}</span>
          <span>Until: {formatDate(certificate.valid_until)}</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleDownload}
            disabled={downloading || certificate.status !== 'active'}
            className="flex-1"
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(verificationUrl, '_blank')}
          >
            <QrCode className="h-4 w-4" />
          </Button>
        </div>

        {/* PBR notice — IP Australia rules require the symbol + warning to travel together. */}
        <PbrNotice className="mt-4" />

        {/* Revoked Info */}
        {certificate.status === 'revoked' && certificate.revoked_at && (
          <div className="mt-4 p-3 bg-error-50 border border-error-200 rounded-lg">
            <p className="text-sm text-error-700">
              <strong>Revoked:</strong> {formatDate(certificate.revoked_at)}
            </p>
            {certificate.revoked_reason && (
              <p className="text-sm text-error-600 mt-1">
                Reason: {certificate.revoked_reason}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CertificateCard;
